import { type Dispatch, type SetStateAction, useCallback, useContext, useState } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import type { Company } from '@core/types/api/Company/Company';
import Enums from '@core/types/api/Enums';
import type { Product } from '@core/types/api/Product/Product';
import type { UnmergedDispatchCustomer } from '@core/types/api/UnmergedDispatchCustomer/UnmergedDispatchCustomer';
import { hasValue } from '@core/utils/equality';
import { ServerErrorMessage, useSlabQueryFetcher } from '@web/hooks/useSlabQuery';
import { useToast } from '@web/providers/ToastProvider/useToast';
import type { QueryRouteBarrelTypes } from '@web/utils/ApiClient';
import type { QueryError } from '@web/utils/Query';

import {
  getAllowedIntegrationKeys,
  getDispatchCustomerMismatchDetails,
} from './useDispatchCustomerMismatch';

export type CheckDispatchCustomersParams = {
  companyId: string | null;
  productsRecord: Record<string, Product | null>;
};

export type DispatchCustomerCheckState = {
  isLinkCustomerModalOpen: boolean;
  isMergeCustomerModalOpen: boolean;
  isCompanyWithoutDispatchModalOpen: boolean;
  isDispatchCustomerMismatchModalOpen: boolean;
  mismatchProducts: Product[];
  selectedCompanyWithMatches: UnmergedDispatchCustomer | null;
  selectedCompany: Company | null;

  setIsLinkCustomerModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsMergeCustomerModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsCompanyWithoutDispatchModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsDispatchCustomerMismatchModalOpen: Dispatch<SetStateAction<boolean>>;
  setMismatchProducts: Dispatch<SetStateAction<Product[]>>;
  setSelectedCompanyWithMatches: Dispatch<SetStateAction<UnmergedDispatchCustomer | null>>;
  setSelectedCompany: Dispatch<SetStateAction<Company | null>>;
};

export type UseDispatchCustomerCheckReturn = DispatchCustomerCheckState & {
  usesDispatchCustomer: boolean;
  checkDispatchCustomers: (params: CheckDispatchCustomersParams) => Promise<boolean>;
  resetDispatchState: () => void;
};

export const useDispatchCustomerCheck = (): UseDispatchCustomerCheckReturn => {
  const ctx = useContext(SlabContext);
  const usesDispatchCustomer = ctx.userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagDispatchCustomer,
  ]);

  const [isLinkCustomerModalOpen, setIsLinkCustomerModalOpen] = useState(false);
  const [isMergeCustomerModalOpen, setIsMergeCustomerModalOpen] = useState(false);
  const [isCompanyWithoutDispatchModalOpen, setIsCompanyWithoutDispatchModalOpen] = useState(false);
  const [isDispatchCustomerMismatchModalOpen, setIsDispatchCustomerMismatchModalOpen] =
    useState(false);
  const [mismatchProducts, setMismatchProducts] = useState<Product[]>([]);
  const [selectedCompanyWithMatches, setSelectedCompanyWithMatches] =
    useState<UnmergedDispatchCustomer | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const companyFetcher = useSlabQueryFetcher<
    'GET company by ID',
    QueryRouteBarrelTypes['GET company by ID'],
    QueryError
  >();
  const dispatchCustomerMatchesFetcher = useSlabQueryFetcher<
    'GET company dispatch customer matches',
    QueryRouteBarrelTypes['GET company dispatch customer matches'],
    QueryError
  >();
  const toast = useToast();

  const resetDispatchState = useCallback(() => {
    setSelectedCompany(null);
    setSelectedCompanyWithMatches(null);
    setMismatchProducts([]);
    setIsLinkCustomerModalOpen(false);
    setIsMergeCustomerModalOpen(false);
    setIsCompanyWithoutDispatchModalOpen(false);
    setIsDispatchCustomerMismatchModalOpen(false);
  }, []);

  const runDispatchCustomerCheck = useCallback(
    async ({ companyId, productsRecord }: CheckDispatchCustomersParams): Promise<boolean> => {
      // if company is not set, no need to check dispatch customers
      if (!hasValue(companyId)) {
        return true;
      }

      // get company with its dispatch customers info
      const company = await companyFetcher('GET company by ID', {
        pathParams: { id: companyId },
      });
      setSelectedCompany(company);

      const hasLinkedDispatchCustomers =
        hasValue(company?.dispatchCustomers) && company?.dispatchCustomers.length > 0;

      const allowedIntegrationKeys = getAllowedIntegrationKeys(company);

      // check if there is a mismatch between the products and company's dispatch customers
      const { productsWithMismatch } = getDispatchCustomerMismatchDetails(
        productsRecord,
        allowedIntegrationKeys,
      );

      if (hasLinkedDispatchCustomers && productsWithMismatch.length > 0) {
        setIsDispatchCustomerMismatchModalOpen(true);
        setMismatchProducts(productsWithMismatch);
        return false;
      }

      const companyDispatchCustomerMatches = await dispatchCustomerMatchesFetcher(
        'GET company dispatch customer matches',
        { pathParams: { id: companyId } },
      );
      const matchInfo = companyDispatchCustomerMatches ?? [];
      const hasSomeMatches = matchInfo.length > 0;

      // if company has linked dispatch customers, no need to check dispatch customers
      if (hasLinkedDispatchCustomers) {
        return true;
      }

      // 1. Handle which modal to open
      if (!hasSomeMatches) {
        // if no matches - open company without dispatch modal
        setIsCompanyWithoutDispatchModalOpen(true);
      } else if (matchInfo.length === 1) {
        // if only one match - open link customer modal
        setIsLinkCustomerModalOpen(true);
      } else {
        // if multiple matches - open merge customer modal
        setIsMergeCustomerModalOpen(true);
      }

      // 2. Set the shared state
      setSelectedCompanyWithMatches({
        id: company.id,
        name: company.name,
        matchInfo,
      });

      return false;
    },
    [companyFetcher, dispatchCustomerMatchesFetcher],
  );

  const checkDispatchCustomers = useCallback(
    (params: CheckDispatchCustomersParams): Promise<boolean> =>
      runDispatchCustomerCheck(params).catch((error) => {
        toast.error({
          message: ServerErrorMessage(error, 'Failed to check the dispatch customer'),
        });
        return false;
      }),
    [runDispatchCustomerCheck, toast],
  );

  return {
    usesDispatchCustomer,
    checkDispatchCustomers,
    resetDispatchState,

    isLinkCustomerModalOpen,
    isMergeCustomerModalOpen,
    isCompanyWithoutDispatchModalOpen,
    isDispatchCustomerMismatchModalOpen,
    mismatchProducts,
    selectedCompanyWithMatches,
    selectedCompany,

    setIsLinkCustomerModalOpen,
    setIsMergeCustomerModalOpen,
    setIsCompanyWithoutDispatchModalOpen,
    setIsDispatchCustomerMismatchModalOpen,
    setMismatchProducts,
    setSelectedCompanyWithMatches,
    setSelectedCompany,
  };
};
